import { useSetAtom } from 'jotai';

import { useDeepEffect, useDeepMemo } from '@crossnokaye/ux-common';

import { EquipmentBrowserSection } from '@repo/sdk/models/components';

import { useEquipmentDetailsRouteState } from '@/features/equipment-browser/equipment-details/hooks/use-equipment-details-route-state';
import { equipmentPoolAtom } from '@/features/equipment-browser/equipment-details/state/selected-equipment.state';
import { useResolvedEquipmentViewId } from '@/features/equipment-browser/hooks/use-resolved-equipment-view-id';
import {
  useGetEquipmentBrowserConfig,
  useGetEquipmentBrowserView,
} from '@/hooks/query-hooks/gaia-api/equipment-browser';
import { useCurrentFacility } from '@/hooks/use-current-facility';

export const useEquipmentPool = () => {
  const { equipmentId, sectionId } = useEquipmentDetailsRouteState();
  const setEquipmentPool = useSetAtom(equipmentPoolAtom);

  const { agentId } = useCurrentFacility();
  const { data: equipmentBrowserConfig } = useGetEquipmentBrowserConfig({ agentId });
  const resolvedViewId = useResolvedEquipmentViewId();
  const { data: equipmentBrowserViewResponse } = useGetEquipmentBrowserView({
    agentId,
    viewId: resolvedViewId,
  });

  const sections = equipmentBrowserViewResponse?.view?.sections;
  const summaryConfigs = equipmentBrowserViewResponse?.summaryConfigs;

  const section: EquipmentBrowserSection | undefined = useDeepMemo(() => {
    if (sectionId) {
      return sections?.find((section) => section.name === sectionId || section.id === sectionId);
    }

    if (equipmentId) {
      return sections?.find((section) => section.order.includes(equipmentId));
    }

    return undefined;
  }, [sections, sectionId, equipmentId]);

  useDeepEffect(() => {
    let equipmentPool =
      summaryConfigs?.filter((summary) => {
        return section ? section.order.includes(summary.referenceId) : true;
      }) ?? [];

    if (section?.order) {
      equipmentPool = equipmentPool.sort((a, b) => {
        const aIndex = section.order.indexOf(a.referenceId);
        const bIndex = section.order.indexOf(b.referenceId);
        return aIndex - bIndex;
      });
    }

    setEquipmentPool(equipmentPool);
  }, [summaryConfigs, section]);

  return {
    isLoading: !equipmentBrowserConfig || !equipmentBrowserViewResponse,
  };
};
